/* Phase 5 · New Job Work Challan form
 * Pick job worker, purpose, add piece rows AND/OR material rows.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";

const PURPOSES = ["Washing", "Finishing", "Repair", "Other"];

export default function NewJobWorkChallan() {
  const nav = useNavigate();
  const [workers, setWorkers] = useState([]);
  const [pieces, setPieces] = useState([]);
  const [form, setForm] = useState({
    job_worker_id: "",
    purpose: "Washing",
    purpose_note: "",
    vehicle_no: "",
    expected_return_date: "",
  });
  const [lines, setLines] = useState([]);        // rows added

  useEffect(() => {
    api.get("/job-workers").then(r => setWorkers(r.data));
    // Only pieces that are in factory (not despatched or at_job_worker)
    api.get("/factory/pieces").then(r => {
      const eligible = r.data.filter(p => !p.at_job_worker && p.current_stage !== "Despatched");
      setPieces(eligible);
    });
  }, []);

  const addPieceLine = () => setLines(ls => [...ls, { kind: "piece", piece_id: "", qty: 1, approx_value: 0, description: "" }]);
  const addMaterialLine = () => setLines(ls => [...ls, { kind: "material", description: "", hsn: "5701", qty: 1, approx_value: 0 }]);
  const removeLine = (i) => setLines(ls => ls.filter((_, idx) => idx !== i));
  const updLine = (i, k, v) => setLines(ls => ls.map((l, idx) => idx === i ? { ...l, [k]: v } : l));

  const submit = async () => {
    if (!form.job_worker_id) return toast.error("Choose a job worker");
    if (lines.length === 0) return toast.error("Add at least one piece or material line");
    try {
      const r = await api.post("/job-work/challans", { ...form, lines });
      toast.success(`Challan ${r.data.challan_no} created`);
      nav(`/coordinator/job-work/${r.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create challan");
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-4xl" data-testid="jw-new-form">
      <h1 className="text-2xl font-bold text-[#0B2340] mb-4">New Job Work Challan</h1>

      <div className="bg-white rounded-lg border border-slate-100 p-4 space-y-3 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="text-sm">
            <span className="text-[#4A627A] text-xs">Job Worker</span>
            <select className="mt-1 w-full border rounded px-2 py-1.5" data-testid="jw-worker-select"
              value={form.job_worker_id} onChange={e => setForm({ ...form, job_worker_id: e.target.value })}>
              <option value="">— select —</option>
              {workers.map(w => <option key={w.id} value={w.id}>{w.name}{w.gstin ? ` · ${w.gstin}` : " · UNREG"}</option>)}
            </select>
          </label>
          <label className="text-sm">
            <span className="text-[#4A627A] text-xs">Purpose</span>
            <select className="mt-1 w-full border rounded px-2 py-1.5" data-testid="jw-purpose"
              value={form.purpose} onChange={e => setForm({ ...form, purpose: e.target.value })}>
              {PURPOSES.map(p => <option key={p}>{p}</option>)}
            </select>
          </label>
          {form.purpose === "Other" && (
            <label className="text-sm md:col-span-2">
              <span className="text-[#4A627A] text-xs">Other purpose note</span>
              <input className="mt-1 w-full border rounded px-2 py-1.5"
                value={form.purpose_note} onChange={e => setForm({ ...form, purpose_note: e.target.value })} />
            </label>
          )}
          <label className="text-sm">
            <span className="text-[#4A627A] text-xs">Expected return date</span>
            <input type="date" className="mt-1 w-full border rounded px-2 py-1.5" data-testid="jw-return-date"
              value={form.expected_return_date} onChange={e => setForm({ ...form, expected_return_date: e.target.value })} />
          </label>
          <label className="text-sm">
            <span className="text-[#4A627A] text-xs">Vehicle no.</span>
            <input className="mt-1 w-full border rounded px-2 py-1.5"
              value={form.vehicle_no} onChange={e => setForm({ ...form, vehicle_no: e.target.value })} />
          </label>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-100 p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-[#0B2340]">Line items</h3>
          <div className="flex gap-2">
            <button onClick={addPieceLine} data-testid="jw-add-piece"
              className="text-xs px-3 py-1 rounded-full bg-[#0B2340] text-white">+ Piece</button>
            <button onClick={addMaterialLine} data-testid="jw-add-material"
              className="text-xs px-3 py-1 rounded-full bg-[#4A627A] text-white">+ Material</button>
          </div>
        </div>
        {lines.length === 0 ? (
          <div className="text-xs text-[#7C8E9E] italic">Add pieces or materials being sent out.</div>
        ) : (
          <div className="space-y-2">
            {lines.map((l, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center text-xs">
                <div className="col-span-1 text-[#7C8E9E] uppercase">{l.kind}</div>
                {l.kind === "piece" ? (
                  <select className="col-span-6 border rounded px-2 py-1" data-testid={`jw-line-${i}-piece`}
                    value={l.piece_id} onChange={e => updLine(i, "piece_id", e.target.value)}>
                    <option value="">— select piece —</option>
                    {pieces.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.code} · {p.design_code} · {p.size_text} · {p.current_stage}
                      </option>
                    ))}
                  </select>
                ) : (
                  <>
                    <input className="col-span-4 border rounded px-2 py-1" placeholder="Description"
                      value={l.description} onChange={e => updLine(i, "description", e.target.value)} />
                    <input className="col-span-2 border rounded px-2 py-1" placeholder="HSN"
                      value={l.hsn} onChange={e => updLine(i, "hsn", e.target.value)} />
                  </>
                )}
                <input type="number" step="0.01" className="col-span-2 border rounded px-2 py-1" placeholder="Qty"
                  value={l.qty} onChange={e => updLine(i, "qty", parseFloat(e.target.value))} />
                <input type="number" step="0.01" className="col-span-2 border rounded px-2 py-1" placeholder="Approx ₹"
                  value={l.approx_value} onChange={e => updLine(i, "approx_value", parseFloat(e.target.value))} />
                <button onClick={() => removeLine(i)} className="col-span-1 text-red-500">×</button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex gap-2">
        <button onClick={submit} data-testid="jw-submit"
          className="px-5 py-2 rounded-full bg-[#B08B49] text-white text-sm hover:bg-[#95753d]">
          Create Challan
        </button>
        <button onClick={() => nav("/coordinator/job-work")}
          className="px-5 py-2 rounded-full bg-white border border-slate-200 text-sm">
          Cancel
        </button>
      </div>
    </div>
  );
}
